// collector/photos.js
//
// Shelf photos for the collector (collector-app P1-C part C): shrink on the
// phone, then upload to `POST /collector-photos` (backend brief
// 2026-09-30-collector-actions). Upload is not attach — the returned `id`
// rides a `facts` action's `photoIds` through the action layer.
//
// No image library: the browser's own decoder and a <canvas> do it (nothing
// in package.json covered this, and a dependency for ~60 lines is not worth
// its bytes on a market phone). Every photo is re-encoded, even a small one:
// that also drops the camera's EXIF, GPS position included.
//
// Two steps, so phase 2 can split them across time: `prepareCollectorPhoto`
// (decode + canvas — needs a document, run it at capture time, while the
// camera's File is still readable) and `sendCollectorPhoto` (no canvas: a
// Blob, a line id and a uuid — plain data a queue can keep and replay).
// `uploadCollectorPhoto` does both, for phase 1's online sheets.
//
// The server is idempotent by the phone's uuid (a resend gets the same id and
// writes nothing), so a failed upload is retried with the SAME uuid.
import { newActionId } from '@/collector/ids.js';
import { postCollectorPhoto } from '@/services/collectorActions.service.js';

/** Longest side after shrinking (spec: 1600 px). */
export const PHOTO_MAX_SIDE = 1600;
/** Target size (spec: ~300 KB; the server takes up to 2 MB). */
export const PHOTO_MAX_BYTES = 300 * 1024;

const QUALITIES = [0.82, 0.72, 0.62, 0.52];
const MAX_SHRINK_ROUNDS = 4;
const RETRY_DELAYS_MS = [1_000, 3_000];

/** Decode a picture. `close()` frees an ImageBitmap; an <img> has nothing to free. */
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
    } catch {
      /* older engines reject the options bag or the format — fall back to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => {} };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toJpeg(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * Shrink a photo: longest side ≤ `maxSide`, JPEG, stepping the quality down
 * and then the size until it is ≤ `maxBytes` (the smallest attempt wins if
 * nothing gets under — a dense shelf at 52% is still far below the 2 MB cap).
 *
 * @param {Blob} file
 * @param {{ maxSide?: number, maxBytes?: number }} [options]
 * @returns {Promise<Blob>} image/jpeg
 */
export async function downscaleImage(file, { maxSide = PHOTO_MAX_SIDE, maxBytes = PHOTO_MAX_BYTES } = {}) {
  const image = await decode(file);
  try {
    if (!image.width || !image.height) throw new Error('collector-photo: empty image');
    let scale = Math.min(1, maxSide / Math.max(image.width, image.height));
    let smallest = null;

    for (let round = 0; round < MAX_SHRINK_ROUNDS; round += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image.source, 0, 0, canvas.width, canvas.height);

      for (const quality of QUALITIES) {
        const blob = await toJpeg(canvas, quality);
        if (!blob) continue;
        if (!smallest || blob.size < smallest.size) smallest = blob;
        if (blob.size <= maxBytes) return blob;
      }
      scale *= 0.8;
    }

    if (!smallest) throw new Error('collector-photo: could not encode');
    return smallest;
  } finally {
    image.close();
  }
}

/**
 * The id the server gives a shelf photo — and so the id a `facts` tap can name
 * it by BEFORE the photo is uploaded (collector-app P2-C: a tap never waits
 * for its photos). `POST /collector-photos` stores the file at
 * `collection-plans/{plan}/item-{line}/app-{uuid}.jpg` and answers with that
 * path as `id` (backend CollectorPhotoController); a `facts` tap's `photoIds`
 * are only checked for that SHAPE on THIS line (CollectorActionApplier::facts),
 * not for the file's existence — so the phone derives the same id from the
 * uuid it will upload under. The FE→BE brief
 * 2026-10-01-collector-app-photos-asks.md asks the backend to keep this derivation a contract.
 *
 * @param {{ planId: string|number, lineId: string|number, uuid: string }} input
 * @returns {string}
 */
export function collectorPhotoPath({ planId, lineId, uuid }) {
  const plan = String(planId ?? '');
  const line = String(lineId ?? '');
  if (!/^[1-9]\d*$/.test(plan) || !/^[1-9]\d*$/.test(line)) {
    throw new TypeError(`collector photo: bad plan "${plan}" or line "${line}"`);
  }
  return `collection-plans/${plan}/item-${line}/app-${String(uuid).toLowerCase()}.jpg`;
}

function retryable(error) {
  const status = error?.response?.status;
  return !status || status >= 500 || status === 408 || status === 429;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Capture-time step: shrink the camera's photo to a small JPEG (canvas — needs
 * a document). Phase 2 keeps the returned Blob, not the camera File.
 * @param {Blob} file
 * @returns {Promise<Blob>}
 */
export function prepareCollectorPhoto(file) {
  return downscaleImage(file);
}

/**
 * Send step: upload an already-shrunk photo. No canvas, no DOM — safe to replay.
 *
 * @param {{ blob: Blob, planItemId: string|number, uuid: string, attempts?: number }} input
 *   `attempts` — tries on a network error or a 5xx, same uuid each time (a
 *   queue with its own backoff passes 1).
 * @returns {Promise<{ id: string, url: string, uuid: string }>}
 */
export async function sendCollectorPhoto({ blob, planItemId, uuid, attempts = 3 }) {
  const tries = Math.max(1, Number(attempts) || 1);
  let lastError = null;
  for (let attempt = 0; attempt < tries; attempt += 1) {
    try {
      const stored = await postCollectorPhoto({ uuid, planItemId, file: blob, filename: `${uuid}.jpg` });
      return { id: stored?.id ?? null, url: stored?.url ?? null, uuid };
    } catch (error) {
      lastError = error;
      if (!retryable(error) || attempt === tries - 1) break;
      await wait(RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]);
    }
  }
  if (lastError && typeof lastError === 'object') lastError.uuid = uuid;
  throw lastError;
}

/**
 * Shrink and upload one shelf photo (phase 1's online path).
 *
 * @param {Blob} file - from the camera input
 * @param {{ planItemId: string|number, uuid?: string, attempts?: number }} options
 *   `uuid` — pass the same one when retrying by hand.
 * @returns {Promise<{ id: string, url: string, uuid: string }>}
 */
export async function uploadCollectorPhoto(file, { planItemId, uuid = newActionId(), attempts = 3 } = {}) {
  const blob = await prepareCollectorPhoto(file);
  return sendCollectorPhoto({ blob, planItemId, uuid, attempts });
}
